import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, IconButton } from '@mui/material';
import { Navigation, RotateCcw, Settings, Maximize2 } from 'lucide-react';

const ChromeStreamerCDP = ({ 
  width = 1500,
  height = 800,
  onUrlChange = () => {},
  style = {},
  apiBaseUrl = 'https://api-morphvm-cudzmim6.http.cloud.morph.so'
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');
  const [inputUrl, setInputUrl] = useState('https://www.google.com');
  const [error, setError] = useState(null);
  const [browserSession, setBrowserSession] = useState(null);
  const [streamConnected, setStreamConnected] = useState(false);
  const [frameMetadata, setFrameMetadata] = useState(null);
  
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const frameRequestRef = useRef(null);
  const initializingRef = useRef(false);

  // Log function for debugging
  const log = useCallback((message, data = null) => {
    console.log(`[ChromeStreamerCDP] ${message}`, data || '');
  }, []);

  // Start browser session
  const startBrowserSession = async () => {
    if (isLoading || initializingRef.current) {
      log('Browser session already initializing, skipping...');
      return;
    }
    
    initializingRef.current = true;
    setIsLoading(true);
    setError(null);
    
    try {
      log('Starting browser session...');
      const response = await fetch(`${apiBaseUrl}/start_browser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enableRemoteAccess: true,
          width,
          height,
          viewport: {
            width,
            height
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to start browser: ${response.status} ${response.statusText}`);
      }

      const sessionData = await response.json();
      log('Browser session response:', sessionData);
      
      if (sessionData.success) {
        setBrowserSession(sessionData);
        setIsConnected(true);
        
        // Extract CDP streaming URL
        const cdpStreamUrl = sessionData.remote_access?.connection?.streaming_endpoints?.cdp_streaming;
        console.log('cdpStreamUrl', cdpStreamUrl);
        if (!cdpStreamUrl) {
          throw new Error('Streaming endpoint not available');
        }
        
        log('Browser session started successfully');
        
        // Navigate to initial URL if provided
        if (inputUrl && inputUrl !== 'about:blank') {
          setTimeout(() => {
            navigateToUrl(inputUrl);
          }, 500);
        }
        
        // Connect to CDP streaming WebSocket
        setTimeout(() => {
          connectCDPStream(cdpStreamUrl);
        }, 1000);
        
      } else {
        throw new Error(sessionData.error || sessionData.message || 'Failed to start browser session');
      }
    } catch (err) {
      log('Error starting browser session:', err);
      setError(`Failed to start browser: ${err.message}`);
    } finally {
      setIsLoading(false);
      initializingRef.current = false;
    }
  };

  // Connect to CDP streaming WebSocket
  const connectCDPStream = (wsUrl) => {
    try {
      log('Connecting to stream:', wsUrl);
      
      // Clean up existing connection
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        log('WebSocket connected');
        setStreamConnected(true);
        setError(null);
        
        // Start streaming
        ws.send(JSON.stringify({
          type: 'start-stream'
        }));
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          switch (message.type) {
            case 'frame':
              renderFrame(message.data, message.metadata);
              break;
              
            case 'stream-started':
              log('CDP stream started:', message.message);
              break;
              
            case 'error':
              log('CDP error:', message.message);
              setError(message.message);
              break;
              
            case 'input-ack':
              // Input acknowledged
              break;
              
            default:
              log('Unknown CDP message:', message);
          }
        } catch (err) {
          log('Error processing CDP message:', err);
        }
      };

      ws.onerror = (error) => {
        log('CDP WebSocket error:', error);
        setError('Streaming connection failed');
      };

      ws.onclose = (event) => {
        log('CDP WebSocket closed:', { code: event.code, reason: event.reason });
        setStreamConnected(false);
        
        // Attempt to reconnect if unexpectedly closed
        if (isConnected && event.code !== 1000) {
          log('Unexpected WebSocket close - attempting to reconnect...');
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isConnected && browserSession) {
              const cdpStreamUrl = browserSession.remote_access?.connection?.streaming_endpoints?.cdp_streaming;
              if (cdpStreamUrl) {
                connectCDPStream(cdpStreamUrl);
              }
            }
          }, 2000);
        }
      };

    } catch (err) {
      log('Error setting up CDP stream:', err);
      setError(`Stream setup failed: ${err.message}`);
    }
  };

  // Track image scaling for coordinate calculation
  const imageScalingRef = useRef({ scale: 1, offsetX: 0, offsetY: 0, imageWidth: 0, imageHeight: 0 });

  // Render frame on canvas
  const renderFrame = (base64Data, metadata) => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Create image from base64 data
    const img = new Image();
    img.onload = () => {
      // Set canvas size to match the browser's actual dimensions
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Store actual image dimensions for coordinate mapping
      imageScalingRef.current = {
        scale: 1,
        offsetX: 0,
        offsetY: 0,
        imageWidth: img.width,
        imageHeight: img.height
      };
      
      // Draw image at full resolution
      ctx.clearRect(0, 0, img.width, img.height);
      ctx.drawImage(img, 0, 0);
      
      // Update metadata
      setFrameMetadata(metadata);
    };
    
    img.src = `data:image/jpeg;base64,${base64Data}`;
  };

  // Handle mouse events on canvas
  const handleCanvasMouseEvent = useCallback((event, eventType) => {
    if (!streamConnected || !wsRef.current || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const scaling = imageScalingRef.current;
    
    // Calculate coordinates relative to the actual image dimensions
    const x = Math.round((event.clientX - rect.left) * (scaling.imageWidth / rect.width));
    const y = Math.round((event.clientY - rect.top) * (scaling.imageHeight / rect.height));
    
    // Determine button
    let button = 'left';
    if (event.button === 1) button = 'middle';
    if (event.button === 2) button = 'right';
    
    // Calculate modifiers
    let modifiers = 0;
    if (event.altKey) modifiers |= 1;
    if (event.ctrlKey) modifiers |= 2;
    if (event.metaKey) modifiers |= 4;
    if (event.shiftKey) modifiers |= 8;
    
    const inputMessage = {
      type: 'input',
      inputType: eventType,
      params: {
        x,
        y,
        button,
        clickCount: event.detail || 1,
        modifiers
      }
    };
    
    wsRef.current.send(JSON.stringify(inputMessage));
  }, [streamConnected, width, height]);

  // Handle mouse wheel
  const handleWheel = useCallback((event) => {
    if (!streamConnected || !wsRef.current || !canvasRef.current) return;
    
    event.preventDefault();
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.round((event.clientX - rect.left) * (width / rect.width));
    const y = Math.round((event.clientY - rect.top) * (height / rect.height));
    
    const inputMessage = {
      type: 'input',
      inputType: 'mouseWheel',
      params: {
        x,
        y,
        deltaX: event.deltaX,
        deltaY: event.deltaY,
        modifiers: 0
      }
    };
    
    wsRef.current.send(JSON.stringify(inputMessage));
  }, [streamConnected, width, height]);

  // Helper function to determine if a key produces printable text
  const isPrintableKey = (key) => {
    // Only single printable characters should have text
    if (key.length === 1) {
      // Check if it's a printable ASCII character
      const code = key.charCodeAt(0);
      return code >= 32 && code <= 126; // Printable ASCII range
    }
    return false;
  };

  // Map JavaScript key names to CDP key names and codes
  const mapKeyForCDP = (key, code) => {
    const keyMappings = {
      // Special keys that need specific mapping
      'Enter': { key: 'Enter', code: 'Enter', keyCode: 13 },
      'Backspace': { key: 'Backspace', code: 'Backspace', keyCode: 8 },
      'Tab': { key: 'Tab', code: 'Tab', keyCode: 9 },
      'Escape': { key: 'Escape', code: 'Escape', keyCode: 27 },
      'Delete': { key: 'Delete', code: 'Delete', keyCode: 46 },
      'Insert': { key: 'Insert', code: 'Insert', keyCode: 45 },
      'Home': { key: 'Home', code: 'Home', keyCode: 36 },
      'End': { key: 'End', code: 'End', keyCode: 35 },
      'PageUp': { key: 'PageUp', code: 'PageUp', keyCode: 33 },
      'PageDown': { key: 'PageDown', code: 'PageDown', keyCode: 34 },
      'ArrowLeft': { key: 'ArrowLeft', code: 'ArrowLeft', keyCode: 37 },
      'ArrowUp': { key: 'ArrowUp', code: 'ArrowUp', keyCode: 38 },
      'ArrowRight': { key: 'ArrowRight', code: 'ArrowRight', keyCode: 39 },
      'ArrowDown': { key: 'ArrowDown', code: 'ArrowDown', keyCode: 40 },
      ' ': { key: ' ', code: 'Space', keyCode: 32 },
      
      // Function keys
      'F1': { key: 'F1', code: 'F1', keyCode: 112 },
      'F2': { key: 'F2', code: 'F2', keyCode: 113 },
      'F3': { key: 'F3', code: 'F3', keyCode: 114 },
      'F4': { key: 'F4', code: 'F4', keyCode: 115 },
      'F5': { key: 'F5', code: 'F5', keyCode: 116 },
      'F6': { key: 'F6', code: 'F6', keyCode: 117 },
      'F7': { key: 'F7', code: 'F7', keyCode: 118 },
      'F8': { key: 'F8', code: 'F8', keyCode: 119 },
      'F9': { key: 'F9', code: 'F9', keyCode: 120 },
      'F10': { key: 'F10', code: 'F10', keyCode: 121 },
      'F11': { key: 'F11', code: 'F11', keyCode: 122 },
      'F12': { key: 'F12', code: 'F12', keyCode: 123 },
      
      // Modifier keys (usually don't send these alone, but just in case)
      'Shift': { key: 'Shift', code: 'ShiftLeft', keyCode: 16 },
      'Control': { key: 'Control', code: 'ControlLeft', keyCode: 17 },
      'Alt': { key: 'Alt', code: 'AltLeft', keyCode: 18 },
      'Meta': { key: 'Meta', code: 'MetaLeft', keyCode: 91 }
    };
    
    return keyMappings[key] || { key, code, keyCode: null };
  };

  // Handle keyboard events
  const handleKeyEvent = useCallback((event, eventType) => {
    if (!streamConnected || !wsRef.current) return;
    
    event.preventDefault();
    
    let modifiers = 0;
    if (event.altKey) modifiers |= 1;
    if (event.ctrlKey) modifiers |= 2;
    if (event.metaKey) modifiers |= 4;
    if (event.shiftKey) modifiers |= 8;
    
    // Map the key to proper CDP format
    const mappedKey = mapKeyForCDP(event.key, event.code);
    
    // Only include text for printable characters on keyDown
    const shouldIncludeText = eventType === 'keyDown' && isPrintableKey(event.key);
    
    const inputMessage = {
      type: 'input',
      inputType: eventType,
      params: {
        key: mappedKey.key,
        code: mappedKey.code,
        keyCode: mappedKey.keyCode,
        modifiers,
        ...(shouldIncludeText && { text: event.key })
      }
    };
    
    wsRef.current.send(JSON.stringify(inputMessage));
  }, [streamConnected]);

  // Helper function to normalize URL by adding https:// if no protocol is present
  const normalizeUrl = (url) => {
    if (!url || typeof url !== 'string') return url;
    
    // Trim whitespace
    const trimmedUrl = url.trim();
    
    // If it's a special URL like about:blank, return as-is
    if (trimmedUrl.startsWith('about:') || trimmedUrl.startsWith('data:') || trimmedUrl.startsWith('file:')) {
      return trimmedUrl;
    }
    
    // If it already has a protocol, return as-is
    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
      return trimmedUrl;
    }
    
    // Add https:// prefix
    return `https://${trimmedUrl}`;
  };

  // Navigate to URL
  const navigateToUrl = async (url) => {
    if (!browserSession) return;
    
    // Normalize the URL to ensure it has a protocol
    const normalizedUrl = normalizeUrl(url);
    
    setIsLoading(true);
    try {
      log('Navigating to:', normalizedUrl);
      const response = await fetch(`${apiBaseUrl}/goto`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: normalizedUrl })
      });

      const result = await response.json();
      if (result.success) {
        setCurrentUrl(normalizedUrl);
        setInputUrl(normalizedUrl);
        onUrlChange(normalizedUrl);
        log('Navigation successful');
      } else {
        throw new Error(result.error || 'Navigation failed');
      }
    } catch (err) {
      log('Navigation error:', err);
      setError(`Navigation failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Disconnect browser session
  const disconnectSession = async () => {
    log('Disconnecting browser session...');
    
    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Stop streaming
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'stop-stream' }));
      wsRef.current.close();
      wsRef.current = null;
    }
    
    // try {
    //   await fetch(`${apiBaseUrl}/stop_browser`, {
    //     method: 'POST'
    //   });
    // } catch (err) {
    //   log('Error stopping browser:', err);
    // }
    
    setIsConnected(false);
    //setBrowserSession(null);
    //setCurrentUrl('');
    //setStreamConnected(false);
    setError(null);
    initializingRef.current = false;
  };

  // Auto-connect on mount
  useEffect(() => {
    let mounted = true;
    
    const initializeBrowser = async () => {
      if (mounted && !initializingRef.current) {
        await startBrowserSession();
      }
    };
    
    initializeBrowser();
    
    return () => {
      mounted = false;
      initializingRef.current = false;
      disconnectSession();
    };
  }, []);

  // Set up canvas event handlers
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Prevent context menu
    const handleContextMenu = (e) => e.preventDefault();
    canvas.addEventListener('contextmenu', handleContextMenu);
    
    return () => {
      canvas.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  return (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        backgroundColor: '#121212',
        ...style 
      }}
    >
      {/* Browser Controls */}
      <Box sx={{ 
        p: 1.5,
        backgroundColor: '#1E1E1E',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <IconButton 
          size="small" 
          onClick={() => navigateToUrl(currentUrl || inputUrl)}
          disabled={!streamConnected}
          sx={{ color: '#fff' }}
          title="Refresh page"
        >
          <RotateCcw size={16} />
        </IconButton>
        
        <TextField
          value={inputUrl}
          onChange={(e) => setInputUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              navigateToUrl(inputUrl);
            }
          }}
          placeholder="Enter URL"
          size="small"
          disabled={!streamConnected}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#2D2D2D',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
              },
            },
            '& .MuiInputBase-input': {
              color: '#fff',
              fontSize: '0.875rem',
            },
          }}
        />
        
        <Button
          variant="contained"
          size="small"
          onClick={() => navigateToUrl(inputUrl)}
          disabled={!streamConnected}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: '#fff',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            }
          }}
        >
          Go
        </Button>

        {/* Connection Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isLoading ? (
            <CircularProgress size={16} />
          ) : streamConnected ? (
            <Box sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              backgroundColor: '#4CAF50' 
            }} />
          ) : isConnected ? (
            <Box sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              backgroundColor: '#FF9800' 
            }} />
          ) : (
            <Box sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              backgroundColor: '#f44336' 
            }} />
          )}
          <Typography variant="caption" sx={{ color: '#888' }}>
            {isLoading ? 'Connecting...' : streamConnected ? 'Streaming' : isConnected ? 'Connected' : 'Disconnected'}
          </Typography>
        </Box>

        {/* Frame info */}
        {frameMetadata && (
          <Typography variant="caption" sx={{ color: '#666' }}>
            {frameMetadata.deviceWidth}×{frameMetadata.deviceHeight}
          </Typography>
        )}
      </Box>

      {/* Browser Canvas */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        backgroundColor: '#000',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <canvas
          ref={canvasRef}
          tabIndex={0}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            cursor: streamConnected ? 'crosshair' : 'default',
            outline: 'none',
            backgroundColor: '#000',
            display: streamConnected ? 'block' : 'none',
            imageRendering: 'crisp-edges'
          }}
          onMouseDown={(e) => handleCanvasMouseEvent(e, 'mouseDown')}
          onMouseUp={(e) => handleCanvasMouseEvent(e, 'mouseUp')}
          onMouseMove={(e) => handleCanvasMouseEvent(e, 'mouseMove')}
          onWheel={handleWheel}
          onKeyDown={(e) => handleKeyEvent(e, 'keyDown')}
          onKeyUp={(e) => handleKeyEvent(e, 'keyUp')}
        />
        
        {/* Overlay content based on state */}
        {error ? (
          <Box sx={{ textAlign: 'center', p: 3, position: 'absolute' }}>
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button 
              variant="outlined" 
              onClick={startBrowserSession}
              sx={{ color: '#fff', borderColor: '#333' }}
            >
              Restart Browser
            </Button>
          </Box>
        ) : !streamConnected && isLoading ? (
          <Box sx={{ textAlign: 'center', position: 'absolute' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography sx={{ color: '#888' }}>
              Setting up stream...
            </Typography>
          </Box>
        ) : !streamConnected && isConnected ? (
          <Box sx={{ textAlign: 'center', position: 'absolute' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography sx={{ color: '#888' }}>
              Establishing connection...
            </Typography>
          </Box>
        ) : !streamConnected && !isConnected ? (
          <Typography sx={{ color: '#888', position: 'absolute' }}>
            Browser not connected
          </Typography>
        ) : null}
      </Box>

      {/* Status Bar */}
      {currentUrl && (
        <Box sx={{ 
          p: 1,
          backgroundColor: '#1E1E1E',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Typography variant="caption" sx={{ color: '#888' }}>
            {currentUrl}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ChromeStreamerCDP; 